import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { generateTask as generateProductTask, generateBiquadraticCountData } from "./ProductFactorsPolynomialEquationTask";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const close = (a, b) => Math.abs(a - b) < 0.001;

const countOptions = [0, 1, 2, 3, 4].map((value) => ({
  value: String(value),
  label: value === 0 ? "brak rozwiązań" : value === 1 ? "1 rozwiązanie" : `${value} rozwiązania`,
}));

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

function formatNumberLatex(value) {
  if (Number.isInteger(value)) return String(value);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  for (let den = 2; den <= 12; den += 1) {
    const num = Math.round(abs * den);
    if (close(num / den, abs)) {
      const g = gcd(num, den);
      return sign + `\\frac{${num / g}}{${den / g}}`;
    }
  }
  return String(Number(value.toFixed(3)));
}

function solutionSetLatex(roots = []) {
  return roots.length ? `\\{${roots.map(formatNumberLatex).join(",")}\\}` : "\\varnothing";
}

function linearFactorLatex({ a = 1, b = 0 }) {
  const xPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  return b === 0 ? xPart : `${xPart}${b > 0 ? "+" : ""}${b}`;
}

function quadraticFactorLatex({ b = 0, c = 0 }) {
  const bx = b === 0 ? "" : b === 1 ? "+x" : b === -1 ? "-x" : `${b > 0 ? "+" : ""}${b}x`;
  const constant = c === 0 ? "" : `${c > 0 ? "+" : ""}${c}`;
  return `x^2${bx}${constant}`;
}

function factorLatex(factor) {
  if (factor.kind === "linear") return linearFactorLatex(factor);
  if (factor.kind === "quadratic") return quadraticFactorLatex(factor);
  return factor.factorLatex || factor.latex;
}

function solveLinear({ a = 1, b = 0 }) {
  return -b / a;
}

function rootsFromFactor(factor) {
  if (factor.kind === "linear") return [solveLinear(factor)];
  return factor.roots || [];
}

function quadraticDelta({ b = 0, c = 0 }) {
  return b * b - 4 * c;
}
function quadraticRootFormulaLines(factor, roots) {
  const delta = quadraticDelta(factor);
  const sqrtDelta = Math.sqrt(delta);
  if (roots.length === 1) {
    return [
      <BlockMath key="square" math={`${factorLatex(factor)}=(x-${formatNumberLatex(roots[0])})^2`} />,
      <BlockMath key="root" math={`(x-${formatNumberLatex(roots[0])})^2=0\\quad\\Rightarrow\\quad x=${formatNumberLatex(roots[0])}`} />,
    ];
  }
  if (roots.length > 1) {
    return [
      <div key="roots" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "start" }}>
        <BlockMath math={`x_1=\\frac{-(${factor.b})-${formatNumberLatex(sqrtDelta)}}{2\\cdot1}=${formatNumberLatex(roots[0])}`} />
        <BlockMath math={`x_2=\\frac{-(${factor.b})+${formatNumberLatex(sqrtDelta)}}{2\\cdot1}=${formatNumberLatex(roots[1])}`} />
      </div>,
    ];
  }
  return [<p key="none">Delta jest ujemna, więc ten czynnik nie daje rozwiązań rzeczywistych.</p>];
}

function parseQuadraticCoefficientsFromLatex(latex = "") {
  const compact = latex.replace(/\s+/g, "").replace(/=0$/, "");
  if (!compact.startsWith("t^2")) return null;
  const rest = compact.slice(3);
  const tIndex = rest.lastIndexOf("t");
  if (tIndex < 0) return null;
  const bRaw = rest.slice(0, tIndex);
  const cRaw = rest.slice(tIndex + 1);
  const b = bRaw === "" || bRaw === "+" ? 1 : bRaw === "-" ? -1 : Number(bRaw);
  const c = Number(cRaw);
  return Number.isFinite(b) && Number.isFinite(c) ? { a: 1, b, c } : null;
}
function buildFromProduct(task, variant) {
  return {
    type: "PolynomialRootCountTask",
    data: {
      variant,
      equationLatex: task.data.equationLatex,
      roots: task.data.roots,
      factors: task.data.factors,
      note: task.data.note,
      noteLatex: task.data.noteLatex,
      formulaLatex: task.data.formulaLatex,
      factorLatex: task.data.factorLatex,
    },
    tags: ["algebra", "rownania", "rownania-wielomianowe", "liczba-rozwiazan"],
  };
}

export function generateTask(variant = "quadraticBlockCount") {
  if (variant === "biquadraticCount") {
    const data = generateBiquadraticCountData();
    return {
      type: "PolynomialRootCountTask",
      data,
      tags: ["algebra", "rownania", "rownania-wielomianowe", "podstawienie"],
    };
  }

  const productVariant = pick(["quadraticBlockSolve", "quadraticBlockSolve", "repeatedRoot", "cubicIdentity"]);
  return buildFromProduct(generateProductTask(productVariant), variant);
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Rozwiąż równanie wielomianowe w zbiorze liczb rzeczywistych.</p>
      <BlockMath math={data.equationLatex} />
      <p>Określ liczbę rozwiązań i wpisz wszystkie rozwiązania.</p>
    </div>
  );
}

export function renderInput(_, userAnswer = {}, onChange, disabled) {
  const count = userAnswer.count ?? "";
  const selectedCount = Number(count);
  const inputCount = Number.isInteger(selectedCount) && selectedCount > 0 ? selectedCount : 0;

  return (
    <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
      <div style={{ maxWidth: 360 }}>
        <SelectInput
          options={countOptions}
          value={count}
          onChange={(e) => onChange({ ...(userAnswer || {}), count: e?.target?.value ?? e })}
          placeholder="Wybierz liczbę rozwiązań"
          disabled={disabled}
        />
      </div>
      {Array.from({ length: inputCount }, (_, index) => {
        const key = `x${index + 1}`;
        return (
          <div key={key} style={{ display: "grid", gap: 6, maxWidth: 520 }}>
            <span style={{ fontWeight: 700 }}><InlineMath math={`x_${index + 1} =`} /></span>
            <FractionInput
              value={userAnswer[key] || ""}
              onChange={(value) => onChange({ ...(userAnswer || {}), [key]: value })}
              disabled={disabled}
              label={`x_${index + 1}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function userRoots(userAnswer = {}) {
  const count = Number(userAnswer.count);
  if (!Number.isInteger(count) || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => userAnswer[`x${index + 1}`]?.parsed)
    .filter((value) => typeof value === "number" && Number.isFinite(value))
    .sort((a, b) => a - b);
}

export function checkAnswer({ data }, userAnswer = {}) {
  if (String(userAnswer.count) !== String(data.roots.length)) return false;
  const actual = userRoots(userAnswer);
  if (actual.length !== data.roots.length) return false;
  return data.roots.every((root, index) => close(root, actual[index]));
}

export function renderUserAnswer(userAnswer = {}) {
  const count = Number(userAnswer.count);
  if (!Number.isInteger(count)) return <i>(brak odpowiedzi)</i>;
  const parts = Array.from({ length: count > 0 ? count : 0 }, (_, index) => {
    const key = `x${index + 1}`;
    const raw = userAnswer?.[key]?.raw;
    return raw ? formatLatexPreview(raw, `x_${index + 1}`) : `x_${index + 1}=\\text{brak}`;
  });
  return (
    <div style={{ textAlign: "left" }}>
      <div><strong>Liczba rozwiązań:</strong> <InlineMath math={String(count)} /></div>
      {parts.length > 0 && <div style={{ marginTop: 6 }}><InlineMath math={parts.join(",\\quad ")} /></div>}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <div><strong>Liczba rozwiązań:</strong> <InlineMath math={String(data.roots.length)} /></div>
      <div style={{ marginTop: 6 }}>
        <strong>Rozwiązania:</strong>{" "}
        {data.roots.length > 0 ? <InlineMath math={`x\\in${solutionSetLatex(data.roots)}`} /> : <span>brak</span>}
      </div>
    </div>
  );
}

function renderFactorCheck(factor, index) {
  if (factor.kind === "linear") {
    const root = solveLinear(factor);
    return (
      <div key={index} style={{ marginTop: 10 }}>
        <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad x=${formatNumberLatex(root)}`} />
      </div>
    );
  }

  if (factor.kind === "quadratic") {
    const roots = rootsFromFactor(factor);
    const delta = quadraticDelta(factor);
    const isSquare = roots.length === 1;
    const isDifference = factor.b === 0 && factor.c < 0;
    const isPositiveSquareSum = factor.b === 0 && factor.c > 0;

    if (isPositiveSquareSum) {
      return (
        <div key={index} style={{ marginTop: 10 }}>
          <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad x^2=${-factor.c}`} />
          <p>Kwadrat liczby rzeczywistej nie może być ujemny, więc ten czynnik nie daje rozwiązań rzeczywistych.</p>
        </div>
      );
    }

    if (isDifference) {
      const q = Math.sqrt(-factor.c);
      return (
        <div key={index} style={{ marginTop: 10 }}>
          <p>Ten czynnik rozpoznajemy jako różnicę kwadratów.</p>
          <BlockMath math={`x^2-${q * q}=0\\quad\\Longleftrightarrow\\quad (x-${q})(x+${q})=0`} />
          <BlockMath math={`x=${formatNumberLatex(-q)}\\quad\\text{lub}\\quad x=${formatNumberLatex(q)}`} />
        </div>
      );
    }

    return (
      <div key={index} style={{ marginTop: 10 }}>
        <p>{isSquare ? "Ten trójmian jest wzorem skróconego mnożenia: kwadratem dwumianu. Dlatego ma jeden pierwiastek podwójny." : "Ten trójmian rozwiązujemy jak zwykłe równanie kwadratowe."}</p>
        <BlockMath math={`\\Delta=${factor.b}^{2}-4\\cdot1\\cdot(${factor.c})=${delta}`} />
        {quadraticRootFormulaLines(factor, roots)}
      </div>
    );
  }

  const roots = rootsFromFactor(factor);
  let comment = "\\text{brak rozwiązań rzeczywistych}";
  if (roots.length === 1) comment = `x=${formatNumberLatex(roots[0])}`;
  if (roots.length > 1) comment = `x\\in${solutionSetLatex(roots)}`;
  return (
    <div key={index} style={{ marginTop: 10 }}>
      <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad ${comment}`} />
    </div>
  );
}

function renderGroupingExpansion(data) {
  if (data.variant !== "grouping") return null;
  const p = data.factors?.[0]?.b || 0;
  const q = Math.sqrt(-(data.factors?.[1]?.c || 0));
  return (
    <>
      <p>Grupujemy wyrazy parami i z każdej grupy wyłączamy wspólny czynnik.</p>
      <BlockMath math={`${data.equationLatex.replace(/=0$/, "")} = x^2(x+${p})-${q * q}(x+${p})`} />
      <p>W obu grupach powstał ten sam nawias, więc wyłączamy go przed nawias.</p>
      <BlockMath math={`x^2(x+${p})-${q * q}(x+${p})=(x+${p})(x^2-${q * q})`} />
      <p>Drugi nawias jest różnicą kwadratów.</p>
      <BlockMath math={`(x+${p})(x^2-${q * q})=(x+${p})(x-${q})(x+${q})`} />
    </>
  );
}
function renderBiquadraticExplanation(data) {
  const tRoots = data.tRoots || [];
  const positiveRoots = tRoots.filter((value) => value > 0);
  const coefficients = data.tCoefficients || parseQuadraticCoefficientsFromLatex(data.tEquationLatex) || { a: 1, b: 0, c: 0 };
  const delta = coefficients.b * coefficients.b - 4 * coefficients.a * coefficients.c;
  const sqrtDelta = Math.sqrt(delta);
  const hasRealTRoots = delta >= 0 && tRoots.length > 0;
  const tRootLines = hasRealTRoots
    ? tRoots.map((value, index) => `t_${index + 1}=${formatNumberLatex(value)}`)
    : ["\\text{brak rozwiązań rzeczywistych dla }t"];
  const xTranslations = positiveRoots.map((value) => {
    const root = Math.sqrt(value);
    return `t=${formatNumberLatex(value)}\\Rightarrow x^2=${formatNumberLatex(value)}\\Rightarrow x=${formatNumberLatex(-root)}\\text{ lub }x=${formatNumberLatex(root)}`;
  });
  const nonPositiveTranslations = tRoots
    .filter((value) => value <= 0)
    .map((value) => `t=${formatNumberLatex(value)}\\Rightarrow x^2=${formatNumberLatex(value)}\\Rightarrow\\text{brak rozwiązań rzeczywistych}`);

  return (
    <>
      <p><strong>Krok 2.</strong> Wprowadzamy podstawienie:</p>
      <BlockMath math="t=x^2" />
      <p>Po podstawieniu otrzymujemy równanie kwadratowe z niewiadomą <InlineMath math="t" />:</p>
      <BlockMath math={data.tEquationLatex} />
      <p>Liczymy deltę tego równania.</p>
      <BlockMath math={`\\Delta=${coefficients.b}^{2}-4\\cdot${coefficients.a}\\cdot(${coefficients.c})=${delta}`} />
      {hasRealTRoots ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "start" }}>
          <BlockMath math={`t_1=\\frac{-(${coefficients.b})-${formatNumberLatex(sqrtDelta)}}{2\\cdot${coefficients.a}}=${formatNumberLatex(tRoots[0])}`} />
          {tRoots.length > 1 && <BlockMath math={`t_2=\\frac{-(${coefficients.b})+${formatNumberLatex(sqrtDelta)}}{2\\cdot${coefficients.a}}=${formatNumberLatex(tRoots[1])}`} />}
        </div>
      ) : (
        <p>Delta jest ujemna, więc równanie w zmiennej <InlineMath math="t" /> nie ma rozwiązań rzeczywistych.</p>
      )}
      <BlockMath math={tRootLines.join("\\quad{}\\text{oraz}\\quad{} ")} />
      <p><strong>Krok 3.</strong> Wracamy do zmiennej <InlineMath math="x" />. Tylko dodatnie wartości <InlineMath math="t" /> dają rozwiązania rzeczywiste, bo <InlineMath math="t=x^2" />.</p>
      {[...xTranslations, ...nonPositiveTranslations].map((math, index) => <BlockMath key={index} math={math} />)}
      {!xTranslations.length && <BlockMath math="\\text{Nie otrzymujemy żadnego rzeczywistego }x." />}
    </>
  );
}
export function renderExplanation({ data }) {
  const equationBody = data.equationLatex.replace(/=0$/, "");
  const hasRealFactorization = data.variant !== "grouping" && data.factorLatex && data.factorLatex !== equationBody;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Szukamy różnych pierwiastków rzeczywistych równania.</p>
      <BlockMath math={data.equationLatex} />
      {renderGroupingExpansion(data)}
      {data.formulaLatex && <BlockMath math={data.formulaLatex} />}
      {hasRealFactorization && <BlockMath math={`${data.factorLatex}=0`} />}
      {data.variant === "biquadraticCount" ? renderBiquadraticExplanation(data) : (
        <>
          {data.note && <p><strong>Uwaga.</strong> {data.note}{data.noteLatex && <> <InlineMath math={data.noteLatex} />.</>}</p>}
          <p><strong>Krok 2.</strong> Sprawdzamy każdy czynnik osobno.</p>
          {(data.factors || []).map(renderFactorCheck)}
        </>
      )}
      <p>
        Równanie ma <InlineMath math={String(data.roots.length)} /> {data.roots.length === 1 ? "rozwiązanie" : "rozwiązania"} w zbiorze liczb rzeczywistych.
      </p>
      <p><strong>Odpowiedź:</strong> <InlineMath math={data.roots.length ? `x\\in${solutionSetLatex(data.roots)}` : "\\text{brak rozwiązań}"} />.</p>
    </div>
  );
}
